import React, {useState} from 'react';
import {
  IconChevronLeft,
  IconChevronRight,
  IconDashboard,
  IconDoc,
  IconFolder,
  IconHome,
  IconSetting,
  IconShieldExclamation,
  IconUser,
  IconUserGroup
} from "../../../components/icons";
import {Header} from "./Header";
import {useAuth} from "../../contexts/AuthContext";
import {toast} from "react-toastify";
import {useHeader} from "../../contexts/HeaderContext";
import MenuItem from "./components/MenuItem";
import {useNavigate} from "react-router-dom";
import packageGsr from "../../../../package.json";

interface SidebarProps {
  children: React.ReactNode;
}

const Sidebar: React.FC<SidebarProps> = ({children}: SidebarProps) => {
  const PUBLIC_URL = process.env.PUBLIC_URL;
  const navigate = useNavigate();
  const {title} = useHeader();
  const {currentUser, getUserRole, getPartenaire, isAdmin, isGestionnaire, logout, canView} = useAuth();
  const [toggle, setToggle] = useState<boolean>(false);

  const handleLogout = () => {
    logout();
    toast.success("Déconnexion réussie");
  }

  return (
    <>
      <aside
        className={`fixed top-0 left-0 h-full sidebar-gradient text-white z-40 transition-all duration-300 ${toggle ? 'w-16' : 'w-60'}`}>
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              // style={{background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)"}}
            >
              <span className="text-xs font-bold">
                <img src={`${PUBLIC_URL}/favicon.ico`} alt={"logo"}/>
              </span>
            </div>
            {(!toggle) && (
              <div className='text-nowrap min-w-0 overflow-hidden'>
                <h1 className="text-sm font-bold">GROUPE SURPLUS</h1>
                {(currentUser) ? (
                  <p className="text-green-400 text-[10px] tracking-widest">
                    {isAdmin ? 'ADMIN' : 'PORTAIL'}
                  </p>
                ) : null}
              </div>
            )}
          </div>
        </div>

        {(currentUser && !toggle && getUserRole() !== '' && isAdmin) ? (
          <div className="mx-3 mt-3 p-2 rounded-lg bg-amber-500/20 text-xs text-nowrap min-w-0 overflow-hidden">
            <span className="text-amber-300">Mode</span>
            <p className="font-semibold text-amber-400">{getUserRole()}</p>
          </div>
        ) : null}

        {(currentUser && !toggle && getUserRole() !== '' && isGestionnaire && getPartenaire().length === 1) ? (
          <div className="mx-3 mt-3 p-2 rounded-lg bg-blue-500/20 text-xs text-nowrap min-w-0 overflow-hidden">
            <span className="text-blue-300">Partenaire</span>
            <p className="font-semibold text-blue-400">{getPartenaire()?.[0]?.nom}</p>
          </div>
        ) : null}

        <nav className="py-3 px-2 space-y-1 mt-2 text-nowrap min-w-0">
          {canView('Accueil') &&
            <MenuItem
              Icon={IconHome}
              title={!toggle && 'Accueil'}
              active='accueil'
              path={'/accueil'}
            />
          }
          {canView('Dossier') &&
            <MenuItem
              Icon={IconFolder}
              title={!toggle && 'Dossiers'}
              active='dossiers'
              path={[
                '/dossiers',
                {state: {isSecondaire: false, currentPage: 1}, replace: true}
              ]}
            />
          }
          {canView('Tableau de bord') &&
            <MenuItem
              Icon={IconDashboard}
              title={!toggle && 'Tableau de bord'}
              active='dashboard'
              path={'/dashboard'}
            />
          }

          {canView('Parametrage') && (
            <MenuItem
              Icon={IconSetting}
              title={!toggle && 'Parametrages'}
              active='parametrage'
            >
              <MenuItem
                Icon={IconUser}
                title={!toggle && 'Utilisateurs'}
                active='utilisateurs'
                path={'/parametrage/utilisateurs'}
                subMenu={true}
              />
              <MenuItem
                Icon={IconShieldExclamation}
                title={!toggle && 'Etat et Choc'}
                active='etat-choc'
                path={'/parametrage/etat-choc'}
                subMenu={true}
              />
              <MenuItem
                Icon={IconUserGroup}
                title={!toggle && 'Rôles utilisateur'}
                active='role-utilisateur'
                path={'/parametrage/role-utilisateur'}
                subMenu={true}
              />
            </MenuItem>
          )}

          <MenuItem
            Icon={IconDoc}
            title={!toggle && 'Documentation'}
            active='documentation'
            path={'/documentation'}
          />
        </nav>

        <button
          onClick={() => setToggle(!toggle)}
          className="absolute -right-3 top-1/2 -mt-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white shadow-lg text-xs"
        >
          {(!toggle) && <IconChevronLeft className="w-3 h-3"/>}
          {(toggle) && <IconChevronRight className="w-3 h-3"/>}
        </button>

        {(currentUser) ? (
          <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-white/10">
            <p
              className={`absolute w-full -top-6 left-0 text-gray-400 font-semibold text-xs ${toggle ? 'text-center' : 'px-3'}`}>
              V{packageGsr.version}
            </p>
            <div
              onClick={() => navigate(`/parametrage/utilisateurs/${currentUser?.id}/modifier`, {state: {user: currentUser}})}
              className="flex items-center gap-2 p-2 rounded hover:bg-[#22C55E19] hover:cursor-pointer"
            >
              <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-green-400 font-semibold text-xs">
                  {currentUser?.nom.charAt(0).toUpperCase()}
                  {currentUser?.prenom.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-white truncate">
                  {currentUser?.nomUtilisateur || `${currentUser?.nom} ${currentUser?.prenom}`}
                </p>
                <p className="text-[10px] text-gray-400 truncate">{currentUser?.email}</p>
              </div>
            </div>
            {(!toggle) &&
                <button
                    onClick={() => handleLogout()}
                    className="mt-3 w-full py-1.5 bg-white/10 hover:bg-white/20 rounded text-xs text-gray-300"
                >
                    Déconnexion
                </button>
            }
          </div>
        ) : null}
      </aside>

      <main className={`flex-1 ${toggle ? 'ml-16' : 'ml-60'} transition-all duration-300`}>
        <Header title={title}/>

        <div className="container animate-in fade-in duration-300 space-y-4">
          {children}
        </div>
      </main>
    </>

  )
};

export default Sidebar;